/**
 * LO QUE LA FOTO SABE DE LA TELA: sus luces y sus pliegues.
 *
 * La foto real de la prenda lleva escrita, en su luminancia, la geometría de la
 * tela: dónde hay un pliegue, una costura, una sombra. Este módulo la lee dos
 * veces, para dos efectos que hacen que el estampado deje de parecer una
 * pegatina:
 *
 *  - LAS LUCES (`lucesDeZona`): las altas luces de la foto, pintadas ENCIMA del
 *    diseño con `screen`, para que en prenda oscura la tela «pise» la tinta.
 *  - LOS PLIEGUES (`desplazamientoDeZona`): un mapa de desplazamiento sacado
 *    del gradiente de luminancia, para DEFORMAR el estampado con un
 *    `feDisplacementMap` y que siga los relieves de la tela en vez de estirarse
 *    plano sobre un cuerpo tridimensional.
 *
 * Los dos se sacan del MISMO recorte de la foto —la parte que cae bajo la
 * zona— y comparten la carga, el recorte y la caché (extraer cuesta un canvas
 * entero). El CORS ya permite leer la foto en canvas: `descargar-mockup.ts` lo
 * hace hoy, así que aquí no hay bloqueo nuevo.
 */

type Pixeles = { data: Uint8ClampedArray; width: number; height: number };

/**
 * La curva que decide qué es «luz», ADAPTADA a cada foto.
 *
 * Un umbral fijo no vale: en una sudadera negra la tela base está en
 * luminancia ~20 y los pliegues en ~40-60 —todo por debajo de cualquier umbral
 * pensado para marino— y en una gris pasa lo contrario. Se mide la propia
 * foto: la MEDIANA es la tela base (con `screen`, cero = neutro, así que la
 * base no debe aclarar nada) y del percentil 95 sale la ganancia que lleva los
 * pliegues más brillantes a una luz visible pero recortada, que nunca borra la
 * tinta.
 */
export function parametrosDeLuces(luminancias: readonly number[]): { umbral: number; ganancia: number } {
  const orden = [...luminancias].sort((a, b) => a - b);
  const p = (q: number) => orden[Math.min(orden.length - 1, Math.floor(orden.length * q))] ?? 0;
  const umbral = p(0.5) + 6; // la base y su ruido no aclaran nada
  const brillos = Math.max(8, p(0.95) - umbral); // margen mínimo: foto plana sin pliegues
  return { umbral, ganancia: Math.min(3, 150 / brillos) };
}

export function curvaDeLuces(pixeles: Pixeles): void {
  const d = pixeles.data;
  const luminancias: number[] = [];
  for (let i = 0; i < d.length; i += 4) {
    luminancias.push(0.2126 * d[i]! + 0.7152 * d[i + 1]! + 0.0722 * d[i + 2]!);
  }
  const { umbral, ganancia } = parametrosDeLuces(luminancias);
  for (let i = 0; i < d.length; i += 4) {
    const luz = Math.max(0, Math.min(200, (luminancias[i / 4]! - umbral) * ganancia));
    d[i] = luz;
    d[i + 1] = luz;
    d[i + 2] = luz;
    d[i + 3] = 255;
  }
}

/** Cuánto empuja un pliegue al estampado. Sube el contraste del relieve. */
const GANANCIA_PLIEGUE = 2.2;
/** Tope del desplazamiento codificado, alrededor del neutro 128. */
const TOPE_PLIEGUE = 96;

/**
 * EL MAPA DE PLIEGUES: el gradiente de luminancia, codificado para
 * `feDisplacementMap`.
 *
 * Un pliegue es un lomo de brillo en la foto. Su PENDIENTE —cuánto y hacia
 * dónde cambia la luminancia— es la dirección en la que la tela se dobla, y por
 * tanto la dirección en la que el estampado tiene que ceder. Se codifica esa
 * pendiente en el color del mapa:
 *
 *   R = 128 + ∂luz/∂x     G = 128 + ∂luz/∂y
 *
 * `feDisplacementMap` desplaza cada punto del estampado por `escala·(canal −
 * 128)/255`: donde la tela es plana (gradiente ≈ 0) el canal vale 128 y no se
 * mueve nada; en el borde de un pliegue, se dobla hacia el relieve. Es una
 * aproximación —no reconstruye la malla 3D del cuerpo— pero saca al estampado
 * del plano por casi nada de coste, y degrada a plano si la foto no se puede
 * leer.
 */
export function mapaDeDesplazamiento(pixeles: Pixeles): void {
  const { data: d, width: w, height: h } = pixeles;
  const luz = new Float32Array(w * h);
  for (let i = 0; i < w * h; i += 1) {
    const o = i * 4;
    luz[i] = 0.2126 * d[o]! + 0.7152 * d[o + 1]! + 0.0722 * d[o + 2]!;
  }
  const at = (x: number, y: number) => luz[Math.max(0, Math.min(h - 1, y)) * w + Math.max(0, Math.min(w - 1, x))]!;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      // Diferencia central: la pendiente local de la luminancia.
      const gx = (at(x + 1, y) - at(x - 1, y)) / 2;
      const gy = (at(x, y + 1) - at(x, y - 1)) / 2;
      const o = (y * w + x) * 4;
      d[o] = 128 + clamp(gx * GANANCIA_PLIEGUE, TOPE_PLIEGUE);
      d[o + 1] = 128 + clamp(gy * GANANCIA_PLIEGUE, TOPE_PLIEGUE);
      d[o + 2] = 128; // canal B neutro: feDisplacementMap solo usa R y G
      d[o + 3] = 255;
    }
  }
}

function clamp(v: number, tope: number): number {
  return Math.max(-tope, Math.min(tope, v));
}

/** Caché por foto, recorte y efecto: extraer cuesta un canvas entero. */
const cache = new Map<string, string | null>();

/**
 * Carga la foto, recorta lo que cae bajo la zona, aplica `transformar` sobre
 * los píxeles y devuelve el resultado como data-URL. Cacheado por foto+recorte+
 * efecto; devuelve `null` si la foto no se puede leer (el mockup queda plano).
 *
 * Se recorta ANTES de procesar: la zona es una fracción de la foto y procesar
 * los 600×600 enteros para enseñar 200×200 sería trabajo tirado.
 */
async function recorteProcesado(
  fotoUrl: string,
  recorte: { izquierdaFrac: number; arribaFrac: number; anchoFrac: number; altoFrac: number },
  efecto: string,
  transformar: (p: Pixeles) => void,
): Promise<string | null> {
  const clave = `${efecto}|${fotoUrl}|${recorte.izquierdaFrac.toFixed(4)},${recorte.arribaFrac.toFixed(4)},${recorte.anchoFrac.toFixed(4)},${recorte.altoFrac.toFixed(4)}`;
  if (cache.has(clave)) return cache.get(clave) ?? null;

  const imagen = await new Promise<HTMLImageElement | null>((resolver) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolver(img);
    // Sin efecto no se rompe nada: el mockup queda plano, que es lo que había.
    img.onerror = () => resolver(null);
    img.src = fotoUrl;
  });
  if (!imagen) {
    cache.set(clave, null);
    return null;
  }

  const sx = recorte.izquierdaFrac * imagen.naturalWidth;
  const sy = recorte.arribaFrac * imagen.naturalHeight;
  const sw = Math.max(1, recorte.anchoFrac * imagen.naturalWidth);
  const sh = Math.max(1, recorte.altoFrac * imagen.naturalHeight);

  const lienzo = document.createElement('canvas');
  lienzo.width = Math.round(sw);
  lienzo.height = Math.round(sh);
  const ctx = lienzo.getContext('2d');
  if (!ctx) return null;

  try {
    ctx.drawImage(imagen, sx, sy, sw, sh, 0, 0, lienzo.width, lienzo.height);
    const pixeles = ctx.getImageData(0, 0, lienzo.width, lienzo.height);
    transformar(pixeles);
    ctx.putImageData(pixeles, 0, 0);
    const url = lienzo.toDataURL('image/png');
    cache.set(clave, url);
    return url;
  } catch {
    // Canvas contaminado (CORS caído) o memoria: el mockup sigue plano.
    cache.set(clave, null);
    return null;
  }
}

/** Las altas luces del recorte de la foto bajo la zona, como data-URL. */
export function lucesDeZona(
  fotoUrl: string,
  recorte: { izquierdaFrac: number; arribaFrac: number; anchoFrac: number; altoFrac: number },
): Promise<string | null> {
  return recorteProcesado(fotoUrl, recorte, 'luces', curvaDeLuces);
}

/** El mapa de pliegues del recorte de la foto bajo la zona, como data-URL. */
export function desplazamientoDeZona(
  fotoUrl: string,
  recorte: { izquierdaFrac: number; arribaFrac: number; anchoFrac: number; altoFrac: number },
): Promise<string | null> {
  return recorteProcesado(fotoUrl, recorte, 'pliegues', mapaDeDesplazamiento);
}
